import Form from "@/components/Shared/Form";
import clsx from "classnames";
import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";
import { Container } from "reactstrap";
import styles from "./styles.module.scss";

// Helper function to decode HTML entities (works server-side and client-side)
const decodeHtmlEntities = (html) => {
  if (!html) return html;

  return html
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'");
};

const extractScriptSrc = (html) => {
  if (!html) return null;
  const match = html.match(/https?:\/\/[^"'\s<>]*hsforms\.net\/forms\/embed\/[^"'\s<>]+\.js/i);
  return match ? match[0] : null;
};

const extractFormDiv = (html) => {
  if (!html) return null;
  const divMatch = html.match(/<div[^>]*class=["']hs-form-frame["'][^>]*>[\s\S]*?<\/div>/i);
  if (divMatch && divMatch[0]) {
    return divMatch[0];
  }

  const formIdMatch = html.match(/data-form-id=["']([^"']+)["']/i);
  const portalIdMatch = html.match(/data-portal-id=["']([^"']+)["']/i);
  const regionMatch = html.match(/data-region=["']([^"']+)["']/i);

  if (formIdMatch && portalIdMatch) {
    const regionAttr = regionMatch ? ` data-region="${regionMatch[1]}"` : "";
    return `<div class="hs-form-frame"${regionAttr} data-form-id="${formIdMatch[1]}" data-portal-id="${portalIdMatch[1]}"></div>`;
  }

  return null;
};

export const HubSpotFormWithColumn = ({
  className,
  heading,
  content,
  image,
  formId,
  hubspotEmbed,
  successPageUrl,
  showLeftcolumn = true,
}) => {
  const router = useRouter();
  const [scriptLoaded, setScriptLoaded] = useState(false);

  const cleanedEmbed = useMemo(() => decodeHtmlEntities(hubspotEmbed || ""), [hubspotEmbed]);
  const initialFormDivHtml = useMemo(() => extractFormDiv(cleanedEmbed), [cleanedEmbed]);
  const [formDivHtml, setFormDivHtml] = useState(initialFormDivHtml);

  const dynamicSuccessPageUrl =
    successPageUrl ||
    (() => {
      const currentPath = router.asPath;
      if (currentPath.includes("?")) {
        const [basePath, queryParams] = currentPath.split("?");
        return `${basePath}/thank-you?${queryParams}`;
      } else {
        return `${currentPath}/thank-you`;
      }
    })();

  // Handle HubSpot embed script loading and form extraction
  useEffect(() => {
    if (!cleanedEmbed) return;

    setFormDivHtml(initialFormDivHtml);

    const scriptSrc = extractScriptSrc(cleanedEmbed);
    if (!scriptSrc) {
      return;
    }

    if (!document.querySelector(`script[src="${scriptSrc}"]`)) {
      const script = document.createElement("script");
      script.src = scriptSrc;
      script.defer = true;
      script.onload = () => setScriptLoaded(true);
      document.body.appendChild(script);
    } else {
      setScriptLoaded(true);
    }
  }, [cleanedEmbed, initialFormDivHtml]);

  return (
    <>
      <section className={clsx(className, styles.root)}>
        <Container>
          {showLeftcolumn ? (
            <div className="wrapper">
              <div>
                {heading && <h2 className={`h3 mb-3`}>{heading}</h2>}
                {content && (
                  <div
                    className={`content`}
                    dangerouslySetInnerHTML={{
                      __html: content,
                    }}
                  />
                )}
                {image && <img src={image?.url} alt={image?.alt} width={"100%"} />}
              </div>
              <div className="w-100">
                {formId && <Form formId={formId} successPageUrl={dynamicSuccessPageUrl} />}
                {formDivHtml && !formId && <div dangerouslySetInnerHTML={{ __html: formDivHtml }} />}
              </div>
            </div>
          ) : (
            <div>
              {heading && (
                <div className="row mb-3">
                  <h2 className={`h3`}>{heading}</h2>
                </div>
              )}
              {content && (
                <div className="row mb-3">
                  <div
                    className={`content`}
                    dangerouslySetInnerHTML={{
                      __html: content,
                    }}
                  />
                </div>
              )}
              <div className="row">
                <div className="w-100">
                  {formId && <Form formId={formId} successPageUrl={dynamicSuccessPageUrl} />}
                  {formDivHtml && !formId && <div dangerouslySetInnerHTML={{ __html: formDivHtml }} />}
                </div>
              </div>
            </div>
          )}
        </Container>
      </section>
    </>
  );
};

export default HubSpotFormWithColumn;
